import { useQuery } from '@apollo/client';

import { EnterpriseSkillsPostAssessmentQuery } from 'bundles/learner-skills/post-assessment-notification/EnterpriseSkillPostAssessmentDataProvider';
import type {
  ProgramCurriculumProductsV1EnrollmentByUserAndCourseQuery as QueryResult,
  ProgramCurriculumProductsV1EnrollmentByUserAndCourseQueryVariables as QueryVariables,
} from 'bundles/naptimejs/resources/__generated__/ProgramCurriculumProductsV1';
import type {
  QueryByCourseIdsQuery,
  QueryByCourseIdsQueryVariables,
} from 'bundles/skills-common/private/skill-advancement/__generated__/queryByCourseIds';
import queryByCourseIds from 'bundles/skills-common/private/skill-advancement/queryByCourseIds.graphql';

type Props = {
  courseId: string;
  userId: number;
};

export const useSkillAdvancementData = ({ courseId, userId }: Props) => {
  const { data: advancementData, error: hasAdvancementDataError } = useQuery<
    QueryByCourseIdsQuery,
    QueryByCourseIdsQueryVariables
  >(queryByCourseIds, {
    variables: { courseIds: [`COURSE~${courseId}`] },
    context: {
      clientName: 'gatewayGql',
    },
  });

  // Reusing the query for the post-assessment notifications, to fetch saved skillsets related to this course
  const { data: savedSkillsetsData, error: hasSavedSkillsetsDataError } = useQuery<QueryResult, QueryVariables>(
    EnterpriseSkillsPostAssessmentQuery,
    {
      variables: { courseId, userId: userId.toString() },
    }
  );

  const allAdvancements =
    advancementData?.SkillScores?.queryByCourseIds?.elements?.map((entry) => ({
      skillId: entry.id,
      skillName: entry.skillName,
      score: entry.scaledSkillScore,
    })) ?? [];

  const advancements = allAdvancements.filter((advancement) => advancement.score > 0);

  const programSlug =
    savedSkillsetsData?.ProgramCurriculumProductsV1?.enrollmentByUserAndCourse?.linked?.enterpriseProgramsV1?.[0]
      ?.metadata.slug;
  const linkedSkillset =
    savedSkillsetsData?.ProgramCurriculumProductsV1?.enrollmentByUserAndCourse?.linked
      ?.enterpriseTargetSkillProfilesV1?.[0];

  const skillset = linkedSkillset
    ? {
        name: linkedSkillset.title,
        slug: linkedSkillset.slug,
        skills: linkedSkillset.targetSkillProficiencies.map((skillProficency) => ({
          skillId: skillProficency.skillId,
          targetScore: skillProficency.targetProficiency,
        })),
      }
    : undefined;

  return { error: hasAdvancementDataError || hasSavedSkillsetsDataError, advancements, programSlug, skillset };
};
